import Vue from "vue";
import { ActionTree, MutationTree } from "vuex";
import { RootState } from "@/store/types";
import { ServerResult } from "@/core/http/server_result";
import { fetchEnumsFromSchema } from "@/core/tools/forms/fetch_enums_from_schema";
import { groupBy } from "lodash";
import moment from "moment";
import { Filesystem } from "@capacitor/core";

interface WarehousesListState {
  loaded: boolean;
  loading: boolean;
  fatalError: boolean;

  lastMeta: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };

  documents: any[];
  days: any[];
  schema: any;

  meta: any;
  warehouse_id: any;
  view: any;
  uploadRemainsCount: number;
}

const state: WarehousesListState = {
  loading: false,
  loaded: false,
  fatalError: false,

  lastMeta: {
    page: 0,
    pageSize: 0,
    totalCount: 0,
    totalPages: 0,
  },

  documents: [],
  days: [],
  schema: null,

  meta: {},
  warehouse_id: null,
  view: null,
  uploadRemainsCount: 0,
};

export const ITEMS_LIST = "GET_WAREHOUSE_REMAINS_LIST";
export const UPLOAD_REMAINS_COUNT = "GET_UPLOAD_REMAINS_COUNT";

export const actions: ActionTree<WarehousesListState, RootState> = {
  loadData({ commit, getters, dispatch }, query) {
    const vm = (this as any)._vm;

    return new Promise((resolve, reject) => {
      commit("CLEAR_DATA");
      commit("START_LOADING");
      commit("SET_QUERY_PARAMS", query);

      const filter: any[] = [];

      filter.push({
        $or: [
          { $null: ["mv.target_date"] },
          query.targetDate
            ? { $eq: ["mv.target_date", { $: query.targetDate }] }
            : {
                $eq: [
                  "mv.target_date",
                  {
                    $: moment()
                      .add(1, "day")
                      .format("YYYY-MM-DD"),
                  },
                ],
              },
        ],
      });
      if (query.warehouse_id) {
        filter.push({ $eq: ["mv.warehouse_id", { $: query.warehouse_id }] });
      }

      const today = moment().format("YYYY-MM-DD");
      const todayStart = `${today} 00:00:00`;
      const todayEnd = moment()
        .add(1, "day")
        .format("YYYY-MM-DD 00:00:00");

      vm.$db([
        vm
          .$q(ITEMS_LIST)
          .raw(query.old_format == "true" ? "warehouse_old" : "warehouse")
          .action({
            warehouse_id: query.warehouse_id,
            date: query.targetDate
              ? query.targetDate
              : moment()
                  .add(1, "day")
                  .format("YYYY-MM-DD"),
          }),
        vm
          .$q(UPLOAD_REMAINS_COUNT)
          .raw("documents.list")
          .action({
            columns: {
              id: "ur.id",
            },
            from: "s:upload_remains",
            from_as: "ur",
            where: {
              $and: [
                {
                  $gte: ["ur.created_at", { $: todayStart }],
                },
                {
                  $lt: ["ur.created_at", { $: todayEnd }],
                },
              ],
            },
            page_size: 1,
            page: 1,
          }),
      ]).then((result: ServerResult) => {
        const uploadRemainsResult = result
          .getOperation(UPLOAD_REMAINS_COUNT)
          .getContent();
        const uploadRemainsCount = uploadRemainsResult.meta
          ? uploadRemainsResult.meta.total_count || 0
          : 0;

        commit("SET_UPLOAD_REMAINS_COUNT", uploadRemainsCount);
        commit("SET_WAREHOUSE_DATA", {
          ...result.getOperation(ITEMS_LIST).getContent(),
          warehouse_id: query.warehouse_id,
        });
        commit("LOADED_SUCCESS");

        resolve(null);
      });
    });
  },
  setSort({ commit }, { field, direction }) {
    commit("SET_SORT", { field, direction });
  },

  toggleSticker({ commit }, idx) {
    commit("TOGGLE_STICKER", idx);
  },
};

export const mutations: MutationTree<WarehousesListState> = {
  START_LOADING: (state) => {
    state.loaded = false;
    state.loading = true;
    state.fatalError = false;
  },
  LOADED_SUCCESS: (state) => {
    state.loaded = true;
    state.loading = false;
    state.fatalError = false;
  },
  LOADED_ERROR: (state) => {
    state.loaded = true;
    state.loading = false;
    state.fatalError = true;
  },
  CLEAR_DATA(state, data) {
    state.days = [];
    state.documents = [];
    state.uploadRemainsCount = 0;
  },
  SET_QUERY_PARAMS(state, data) {
    state.view = data.view;
    state.warehouse_id = data.warehouse_id;
  },
  SET_UPLOAD_REMAINS_COUNT(state, count) {
    state.uploadRemainsCount = count;
  },
  TOGGLE_STICKER(state, idx) {
    state.documents[idx].expanded = !state.documents[idx].expanded;
  },

  SET_SORT(state, { field, direction }) {
    state.documents = state.documents.sort((a: any, b: any) => {
      const keyA =
        a.sticker_name + ":" + a.product_name + ":" + a.product_container_name;
      const keyB =
        b.sticker_name + ":" + b.product_name + ":" + b.product_container_name;
      return keyA.localeCompare(keyB);
    });
  },

  SET_WAREHOUSE_DATA(state, data) {
    state.days = data.days.map((day: any) => moment(day).format("DD.MM.YYYY"));
    state.days.splice(state.days.length - 1, 1);
    state.documents = Object.values(
      data.data
        .map((row: any) => {
          const border_date = moment(row.last_deals);
          if (!row.last_deals) {
            row.class = "is-no-deals";
          } else {
            if (
              moment()
                .subtract(1, "month")
                .isAfter(border_date)
            ) {
              row.class = "is-hold";
            }
            if (
              moment()
                .subtract(2, "month")
                .isAfter(border_date)
            ) {
              row.class = "is-stuck";
            }
          }
          const qtyNum = parseFloat(row.quantity) || 0;
          row.remains = qtyNum * parseFloat(row.product_container_weight);
          row.valid =
            qtyNum != 0 ||
            parseFloat(row.remains) != 0 ||
            parseInt(row.availability) != 0 ||
            parseInt(row.resort) != 0 ||
            parseInt(row.mes_exported) != 0;
          row.weight = qtyNum * parseFloat(row.product_container_weight);
          row.columns = row.expected.map((item: any, idx: number) => {
            const product_container_weight = parseFloat(
              row.product_container_weight,
            );
            row.remains -= parseFloat(item) * product_container_weight;
            const shouldUseMes = state.uploadRemainsCount === 0;
            const mesValue =
              parseFloat(row.mes[idx]) * product_container_weight;
            const col = {
              expected: parseFloat(item) * product_container_weight,
              remains:
                parseFloat(row.remains) -
                (parseFloat(row.movement[idx]) +
                  parseFloat(row.mes_exported[idx])) *
                  product_container_weight,
              movement:
                parseFloat(row.movement[idx]) * product_container_weight,
              availability: row.availability[idx] * product_container_weight,
              resort: parseFloat(row.resort[idx]) * product_container_weight,
              mes: mesValue,
              mes_exported:
                parseFloat(row.mes_exported[idx]) * product_container_weight,
              remains_total: 0,
            };
            row.remains += col.availability;
            row.remains -= col.movement;
            row.remains += col.resort;
            if (shouldUseMes) {
              row.remains += col.mes;
            }
            row.remains += col.mes_exported;

            row.valid =
              row.valid ||
              col.availability != 0 ||
              col.movement != 0 ||
              col.resort != 0 ||
              col.mes != 0 ||
              col.mes_exported != 0 ||
              col.expected != 0 ||
              col.remains != 0;
            col.remains_total = Math.round((row.remains + 0) * 100) / 100;
            return col;
          });
          if (row.columns.length > 1) {
            row.columns.splice(row.columns.length - 1, 1);
          }
          return row;
        })
        .filter((item: any) => {
          return item.valid;
        })
        .reduce((acc: any, item: any) => {
          const groupKey =
            data.warehouse_id == "d:warehouses:6" ? item.sticker_id : "";
          if (!acc[groupKey]) {
            acc[groupKey] = [];
          }
          acc[groupKey].push(item);
          return acc;
        }, {}),
    )
      .reduce((acc: any[], item: any) => {
        const sticker: any = {
          sticker_id: item[0].sticker_id,
          sticker_name: !item[0].sticker_id
            ? "Без стикера"
            : item[0].sticker_name,
          expanded: !item[0].sticker_id,
          product_name: "",
          product_container_weight: item[0].product_container_weight,
          quantity: 0,
          weight: 0,
          remains: 0,
          valid: 0,
          class: "sticker",
          columns: [],
        };
        const items: any[] = [];
        item
          .sort((a: any, b: any) => {
            const keyA = a.product_name + ":" + a.product_container_name;
            const keyB = b.product_name + ":" + b.product_container_name;
            return keyA.localeCompare(keyB);
          })
          .map((row: any) => {
            sticker.quantity += parseFloat(row.quantity) || 0;
            sticker.weight += parseFloat(row.weight);
            sticker.remains += parseFloat(row.remains);
            row.columns.forEach((c: any, idx: number) => {
              if (!sticker.columns[idx]) {
                sticker.columns[idx] = {
                  expected: 0,
                  weight: 0,
                  resort: 0,
                  resort_count: 0,
                  mes: 0,
                  mes_exported: 0,
                  remains: 0,
                  movement: 0,
                  availability: 0,
                  remains_total: 0,
                };
              }
              sticker.columns[idx].weight += c.weight;
              sticker.columns[idx].expected += c.expected;
              sticker.columns[idx].resort += Math.round(c.resort * 100) / 100;
              sticker.columns[idx].resort_count += c.resort != 0 ? 1 : 0;
              sticker.columns[idx].mes += Math.round(c.mes * 100) / 100;
              sticker.columns[idx].mes_exported +=
                Math.round(c.mes_exported * 100) / 100;
              sticker.columns[idx].remains += c.remains;
              sticker.columns[idx].movement += c.movement;
              sticker.columns[idx].availability += c.availability;
              sticker.columns[idx].remains_total += c.remains_total;
            });
            items.push(row);
          });
        if (data.warehouse_id == "d:warehouses:6") {
          sticker.items = items;
          acc.push(sticker);
        } else {
          acc.push(...items);
        }
        return acc;
      }, [])
      .sort((a: any, b: any) => {
        const keyA = a.sticker_id ? a.sticker_name.toLowerCase() : "я";
        const keyB = b.sticker_id ? b.sticker_name.toLowerCase() : "я";
        return keyA.localeCompare(keyB, "en");
      });

    state.lastMeta = {
      page: data.meta.page,
      pageSize: data.meta.page_size,
      totalCount: data.meta.total_count,
      totalPages: data.meta.total_pages,
    };
  },
};

const getters = {
  total(state: WarehousesListState) {
    const stickers_only: boolean =
      state.documents.filter((item: any) => item.class != "sticker").length ==
      0;
    return {
      quantity: state.documents.reduce((prev: any, field: any) => {
        return (
          prev +
          (field.class != "sticker" || stickers_only ? field.quantity : 0)
        );
      }, 0),
      weight: state.documents.reduce((prev: any, field: any) => {
        return (
          prev + (field.class != "sticker" || stickers_only ? field.weight : 0)
        );
      }, 0),
      columns: state.documents.reduce((prev: any, field: any) => {
        if (field.class != "sticker" || stickers_only) {
          field.columns.map((item: any, idx: number) => {
            if (!prev[idx]) {
              prev[idx] = {
                weight: 0,
                expected: 0,
                resort: 0,
                mes: 0,
                mes_exported: 0,
                resort_count: 0,
                remains: 0,
                availability: 0,
                movement: 0,
                remains_total: 0,
              };
            }
            prev[idx].expected += item.expected;
            prev[idx].resort += Math.round(item.resort * 100) / 100;
            prev[idx].mes += Math.round(item.mes * 100) / 100;
            prev[idx].mes_exported += Math.round(item.mes_exported * 100) / 100;

            prev[idx].resort_count += item.resort_count;
            prev[idx].remains += item.remains;
            prev[idx].availability += item.availability;
            prev[idx].movement += item.movement;
            prev[idx].remains_total += item.remains_total;
          });
        }
        return prev;
      }, []),
    };
  },
  exportData(state: WarehousesListState) {
    const stickers_only: boolean =
      state.documents.filter((item: any) => item.class != "sticker").length ==
      0;
    const csvContent: any[] = [];
    const header = ["Артикул", "Продукт", "Тара"];
    header.push(`Остаток на ${state.days[0]}`);

    const columns = state.documents.reduce((prev: any, field: any) => {
      if (field.class != "sticker" || stickers_only) {
        field.columns.map((item: any, idx: number) => {
          if (!prev[idx]) {
            prev[idx] = {
              expected: 0,
              weight: 0,
              resort: 0,
              mes: 0,
              mes_exported: 0,
              remains: 0,
              availability: 0,
              movement: 0,
              remains_total: 0,
            };
          }
          prev[idx].expected += item.expected;
          prev[idx].resort += Math.round(item.resort * 100) / 100;
          prev[idx].mes += Math.round(item.mes * 100) / 100;
          prev[idx].mes_exported += Math.round(item.mes_exported * 100) / 100;

          prev[idx].resort_count += item.resort_count;
          prev[idx].remains += item.remains;
          prev[idx].availability += item.availability;
          prev[idx].movement += item.movement;
          prev[idx].remains_total += item.remains_total;
        });
      }
      return prev;
    }, []);
    state.days.map((day: any, idx: number) => {
      if (state.view !== "total") {
        if (columns[idx].mes_exported > 0) {
          header.push(`Из MES на  ${day}`);
        }
        header.push(`Заявки на ${day}`);
        if (columns[idx].movement > 0) {
          header.push(`Перемещения со склада на  ${day}`);
        }
        if (columns[idx].resort_count > 0) {
          header.push(`Перегруппировка на ${day}`);
        }
        if (columns[idx].availability > 0) {
          header.push(`В пути на  ${day}`);
        }
        if (columns[idx].mes > 0) {
          header.push(`На созревании на  ${day}`);
        }
        header.push(`Остаток на конец дня`);
      }
    });
    if (state.view == "total") {
      header.push("Остаток на следующий день");
    }

    state.documents.map((item: any) => {
      const row: any[] = [];
      row.push(item.sku_mixcart);
      row.push(item.class == "sticker" ? item.sticker_name : item.product_name);
      row.push(item.product_container_name);
      row.push(item.weight);
      // row.push(item.quantity)
      item.columns.map((c: any, idx: number) => {
        if (state.view !== "total") {
          if (columns[idx].mes_exported > 0) {
            row.push(c.mes_exported);
          }
          row.push(c.expected);
          if (columns[idx].movement > 0) {
            row.push(c.movement);
          }
          if (columns[idx].resort_count > 0) {
            row.push(c.resort);
          }
          if (columns[idx].availability > 0) {
            row.push(c.availability);
          }
          if (columns[idx].mes > 0) {
            row.push(c.mes);
          }
          row.push(c.remains_total);
        }
      });
      if (state.view == "total") {
        row.push(item.columns[item.columns.length - 1].remains_total);
      }
      csvContent.push(row);
      if (item.items) {
        item.items.map((i: any) => {
          const row: any[] = [];
          row.push(i.sku_mixcart);
          row.push(i.class == "sticker" ? i.sticker_name : i.product_name);
          row.push(i.product_container_name);
          row.push(i.quantity * i.product_container_weight);
          // row.push(i.quantity)
          i.columns.map((c: any, idx: number) => {
            if (state.view !== "total") {
              if (columns[idx].mes_exported > 0) {
                row.push(c.mes_exported);
              }
              row.push(c.expected);
              if (columns[idx].movement > 0) {
                row.push(c.movement);
              }
              if (columns[idx].resort_count > 0) {
                row.push(c.resort);
              }
              if (columns[idx].availability > 0) {
                row.push(c.availability);
              }
              if (columns[idx].mes > 0) {
                row.push(c.mes);
              }
              row.push(c.remains_total);
            }
          });
          if (state.view == "total") {
            row.push(i.columns[i.columns.length - 1].remains_total);
          }
          csvContent.push(row);
        });
      }
    });
    return [header, ...csvContent];
  },
};

export const STORE_KEY = "$_warehouses_list";

export const store = {
  namespaced: true,
  state,
  actions,
  getters,
  mutations,
};
